import { useEffect, useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { apiFetch } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { useMyDIDs } from '../api/hooks';
import ZKAnchorBadge, { ZKAnchorStatus } from '../components/ZKAnchorBadge';
import ZKAnchorTimeline from '../components/ZKAnchorTimeline';
import { txUrl, shortHash } from '../lib/chainExplorer';
import { encodePathSegment } from '../lib/url';
import { useWeb3 } from '../contexts/Web3Context';

/** The wallet address a DID is controlled by (did:adi[:<chain>]:0x…), or null for key-based DIDs. */
function walletAddressOf(did: string): string | null {
  const m = did.match(/:(0x[0-9a-fA-F]{40})$/);
  return m ? m[1] : null;
}

// Dropdown populated from the user's registered DIDs with a free-text fallback.
function DIDSelector({
  value,
  onChange,
  onBlur,
  hasError,
  placeholder,
}: {
  value: string;
  onChange: (did: string) => void;
  onBlur?: () => void;
  hasError?: boolean;
  placeholder?: string;
}) {
  const { data: myDIDs, loading } = useMyDIDs();
  const activeDIDs = myDIDs?.filter((d) => d.status === 'active') ?? [];
  const CUSTOM = '__custom__';
  const [mode, setMode] = useState<'select' | 'custom'>('select');

  // Auto-select first DID when the list loads and nothing is chosen yet.
  useEffect(() => {
    if (activeDIDs.length > 0 && !value) {
      onChange(activeDIDs[0].did);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeDIDs.length]);

  const borderClass = hasError
    ? 'border-red-300 focus:border-red-400 focus:ring-red-200'
    : 'border-border-subtle focus:border-primary focus:ring-primary';

  if (loading) {
    return (
      <select disabled className={`w-full rounded-lg border px-3 py-2 text-sm bg-slate-50 text-text-faint ${borderClass}`}>
        <option>Loading your DIDs…</option>
      </select>
    );
  }

  if (activeDIDs.length === 0 || mode === 'custom') {
    return (
      <div className="space-y-1">
        <input
          type="text"
          className={`w-full rounded-lg border px-3 py-2 text-sm transition-colors focus:outline-none focus:ring-1 ${borderClass}`}
          placeholder={placeholder ?? 'did:adi:...'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
        />
        {activeDIDs.length > 0 && (
          <button
            type="button"
            onClick={() => { setMode('select'); onChange(activeDIDs[0].did); }}
            className="text-[10px] font-semibold text-primary hover:underline"
          >
            ← Pick from my DIDs
          </button>
        )}
        {activeDIDs.length === 0 && (
          <p className="text-[10px] text-text-faint">No active DIDs found — <a href="/my-dids" className="text-primary hover:underline">create one first</a>.</p>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-1">
      <select
        className={`w-full rounded-lg border px-3 py-2 text-sm transition-colors focus:outline-none focus:ring-1 bg-white ${borderClass}`}
        value={activeDIDs.find((d) => d.did === value) ? value : (value ? CUSTOM : activeDIDs[0].did)}
        onChange={(e) => {
          if (e.target.value === CUSTOM) {
            setMode('custom');
            onChange('');
          } else {
            onChange(e.target.value);
          }
        }}
        onBlur={onBlur}
      >
        {activeDIDs.map((d) => (
          <option key={d.did} value={d.did} title={d.did}>
            {d.did.length > 48 ? d.did.slice(0, 20) + '…' + d.did.slice(-16) : d.did}
          </option>
        ))}
        <option value={CUSTOM}>Custom DID…</option>
      </select>
    </div>
  );
}

type Tab = 'prove' | 'verify' | 'delegate';

interface Predicate {
  id: string;
  attributeName: string;
  type: string;
  value?: string;
  rangeMin?: string;
  rangeMax?: string;
  memberSet?: string;
}

interface AnchorRecord {
  proofId: string;
  txHash: string;
  blockNumber: number | null;
  status: ZKAnchorStatus | string;
  submittedAt?: string;
  confirmedAt?: string | null;
  revokedAt?: string | null;
  revokedBlock?: number | null;
  failureReason?: string | null;
  lagBlocks?: number;
}

const PREDICATE_TYPES = ['gt', 'gte', 'lt', 'lte', 'eq', 'range', 'membership', 'non_membership'] as const;

function CopyButton({ text }: { text: string }) {
  const { t } = useTranslation('zk');
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <button
      onClick={handleCopy}
      className="absolute right-2 top-2 rounded-md bg-slate-100 px-2 py-1 text-xs font-bold text-text-faint hover:bg-slate-200 transition-colors"
    >
      {copied ? (
        <span className="flex items-center gap-1 text-emerald-600">
          <span className="material-symbols-outlined" style={{ fontSize: 12 }}>check</span>
          {t('verify.copied')}
        </span>
      ) : (
        t('verify.copy')
      )}
    </button>
  );
}

function FieldError({ show, message }: { show: boolean; message: string }) {
  if (!show) return null;
  return <p className="mt-1 text-xs text-red-600">{message}</p>;
}

export default function ZKIdentity() {
  const [tab, setTab] = useState<Tab>('prove');
  // Bumped by ProveTab after a successful anchor so the sibling
  // AnchoredProofsList below refetches (it cannot observe ProveTab's state).
  const [anchorRefresh, setAnchorRefresh] = useState(0);
  const { t } = useTranslation('zk');
  const { wallet } = useAuth();
  const myDid = wallet?.did ?? null;

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-text-main">{t('title')}</h1>
        <p className="mt-1 text-sm text-text-muted">{t('subtitle')}</p>
      </div>

      <div className="flex gap-1 rounded-lg border border-border-subtle bg-slate-50 p-1">
        <button
          onClick={() => setTab('prove')}
          className={`flex-1 rounded-md px-4 py-2 text-sm font-bold transition-colors ${
            tab === 'prove' ? 'bg-white text-primary shadow-sm' : 'text-text-faint hover:text-text-main'
          }`}
        >
          <span className="material-symbols-outlined mr-2 align-middle" style={{ fontSize: 16 }}>shield_lock</span>
          {t('tabs.prove')}
        </button>
        <button
          onClick={() => setTab('verify')}
          className={`flex-1 rounded-md px-4 py-2 text-sm font-bold transition-colors ${
            tab === 'verify' ? 'bg-white text-primary shadow-sm' : 'text-text-faint hover:text-text-main'
          }`}
        >
          <span className="material-symbols-outlined mr-2 align-middle" style={{ fontSize: 16 }}>verified</span>
          {t('tabs.verify')}
        </button>
        <button
          onClick={() => setTab('delegate')}
          className={`flex-1 rounded-md px-4 py-2 text-sm font-bold transition-colors ${
            tab === 'delegate' ? 'bg-white text-primary shadow-sm' : 'text-text-faint hover:text-text-main'
          }`}
        >
          <span className="material-symbols-outlined mr-2 align-middle" style={{ fontSize: 16 }}>handshake</span>
          {t('tabs.delegate')}
        </button>
      </div>

      {tab === 'prove' && <ProveTab onAnchored={() => setAnchorRefresh((n) => n + 1)} />}
      {tab === 'verify' && <VerifyTab />}
      {tab === 'delegate' && <DelegateTab />}

      {/* Persistent "Anchored proofs" feed at the bottom of the page (§2a).
          ProveTab is a sibling, so a successful anchor bumps this counter to
          force the list to refetch — otherwise it only reloads when the DID
          changes and a new anchor never appears. */}
      <AnchoredProofsList did={myDid} refreshKey={anchorRefresh} />
    </div>
  );
}

function ProveTab({ onAnchored }: { onAnchored?: () => void }) {
  const { t } = useTranslation('zk');
  const [credentialId, setCredentialId] = useState('');
  const [proverDid, setProverDid] = useState('');
  const [privateKey, setPrivateKey] = useState('');
  const [showKey, setShowKey] = useState(false);
  // A wallet-controlled DID authorizes the proof with a MetaMask signature, so
  // no private key is needed (or ever leaves the wallet). The Ed25519 key path
  // stays available for key-based DIDs and as a fallback.
  const { account, connect, signer } = useWeb3();
  const [useKeyInstead, setUseKeyInstead] = useState(false);
  const proverWallet = walletAddressOf(proverDid);
  const walletMode = !!proverWallet && !useKeyInstead;
  const walletMatches = !!account && !!proverWallet && account.toLowerCase() === proverWallet.toLowerCase();
  const [subjectJson, setSubjectJson] = useState('{\n  "age": 25,\n  "country": "US"\n}');
  const [credentials, setCredentials] = useState<Array<{ id: string; schema: string; issuedAt: string }>>([]);
  const [credsLoading, setCredsLoading] = useState(false);
  const [predicates, setPredicates] = useState<Predicate[]>([
    { id: crypto.randomUUID(), attributeName: 'age', type: 'gte', value: '18' },
  ]);
  const [anchorOnChain, setAnchorOnChain] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [generatedProofId, setGeneratedProofId] = useState<string | null>(null);
  const [anchor, setAnchor] = useState<AnchorRecord | null>(null);
  const [anchorBusy, setAnchorBusy] = useState(false);
  const [anchorError, setAnchorError] = useState<string | null>(null);
  const [pendingPolls, setPendingPolls] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  const markTouched = useCallback((field: string) => {
    setTouched((tt) => ({ ...tt, [field]: true }));
  }, []);

  const addPredicate = () => {
    setPredicates([...predicates, { id: crypto.randomUUID(), attributeName: '', type: 'gte', value: '' }]);
  };

  const removePredicate = (idx: number) => {
    setPredicates(predicates.filter((_, i) => i !== idx));
  };

  const updatePredicate = (idx: number, field: keyof Predicate, val: string) => {
    setPredicates((prev) => {
      const updated = [...prev];
      updated[idx] = { ...updated[idx], [field]: val };
      return updated;
    });
  };

  // Poll anchor status while pending. Capped at 12 polls (1 minute).
  useEffect(() => {
    if (!generatedProofId) return;
    if (!anchor || anchor.status !== 'pending') return;
    if (pendingPolls >= 12) return;

    const handle = window.setTimeout(async () => {
      try {
        const fresh = await apiFetch<AnchorRecord>(`/zkp/proofs/${encodePathSegment(generatedProofId)}/anchor`);
        setAnchor(fresh);
      } catch {
        // Keep prior state; lag in indexer or 404 before propagation.
      } finally {
        setPendingPolls((n) => n + 1);
      }
    }, 5000);
    return () => window.clearTimeout(handle);
  }, [generatedProofId, anchor, pendingPolls]);

  // Fetch credentials held by the selected DID and clear selection when DID changes.
  useEffect(() => {
    setCredentialId('');
    if (!proverDid) { setCredentials([]); return; }
    setCredsLoading(true);
    apiFetch<{ credentials?: Array<{ id: string; type?: string[]; issuanceDate?: string; credentialSubject?: Record<string, unknown> }>; items?: Array<{ id: string; schema: string; issuedAt: string; credentialSubject?: Record<string, unknown> }> }>(
      `/credentials?subject=${encodeURIComponent(proverDid)}&status=active&limit=50`,
    )
      .then((res) => {
        const raw = res.credentials ?? res.items ?? [];
        const list = raw.map((vc) => ({
          id: vc.id,
          schema: Array.isArray(vc.type) ? (vc.type.find((t) => t !== 'VerifiableCredential') ?? vc.id) : vc.id,
          issuedAt: (vc as Record<string, unknown>).issuanceDate as string ?? '',
          credentialSubject: vc.credentialSubject,
        }));
        setCredentials(list);
      })
      .catch(() => setCredentials([]))
      .finally(() => setCredsLoading(false));
  }, [proverDid]);

  // When a credential is selected, auto-fill credentialSubject from cached list or fetch.
  const handleCredentialSelect = async (id: string) => {
    setCredentialId(id);
    if (!id) return;
    // Try cached data first
    const cached = credentials.find((c) => c.id === id);
    if (cached?.credentialSubject) {
      // Strip the 'id' field (holder DID) — it's not a claim field
      const { id: _subjectId, ...claims } = cached.credentialSubject as Record<string, unknown>;
      void _subjectId;
      setSubjectJson(JSON.stringify(claims, null, 2));
      return;
    }
    try {
      const vc = await apiFetch<{ credentialSubject?: Record<string, unknown> }>(`/credentials/${encodePathSegment(id)}`);
      if (vc.credentialSubject) {
        const { id: _subjectId, ...claims } = vc.credentialSubject;
        void _subjectId;
        setSubjectJson(JSON.stringify(claims, null, 2));
      }
    } catch {
      // keep existing subject JSON if fetch fails
    }
  };

  const handleGenerate = async () => {
    setTouched({ credentialId: true, proverDid: true, privateKey: true });
    if (!credentialId || !proverDid) return;
    if (walletMode ? !walletMatches || !signer : !privateKey) return;

    setLoading(true);
    setError(null);
    setResult(null);
    setAnchor(null);
    setGeneratedProofId(null);
    setAnchorError(null);
    setPendingPolls(0);
    try {
      let subject: Record<string, unknown>;
      try {
        subject = JSON.parse(subjectJson);
      } catch {
        throw new Error(t('prove.errorInvalidSubject'));
      }

      const validationErrors: string[] = [];
      predicates.forEach((p, i) => {
        if (!p.attributeName) validationErrors.push(t('prove.errorAttributeRequired', { index: i + 1 }));
        if (p.type === 'range') {
          if (!p.rangeMin || !p.rangeMax) validationErrors.push(t('prove.errorRangeRequired', { index: i + 1 }));
        } else if (p.type === 'membership' || p.type === 'non_membership') {
          if (!p.memberSet?.trim()) validationErrors.push(t('prove.errorMemberSetRequired', { index: i + 1 }));
        } else {
          if (p.value === undefined || p.value === '') validationErrors.push(t('prove.errorValueRequired', { index: i + 1 }));
        }
      });
      if (validationErrors.length > 0) {
        throw new Error(validationErrors.join('. '));
      }

      const apiPredicates = predicates.map((p) => {
        const pred: Record<string, unknown> = { attributeName: p.attributeName, type: p.type };
        if (p.type === 'range') {
          pred.rangeMin = Number(p.rangeMin);
          pred.rangeMax = Number(p.rangeMax);
        } else if (p.type === 'membership' || p.type === 'non_membership') {
          pred.memberSet = p.memberSet?.split(',').map((s) => s.trim()) ?? [];
        } else {
          pred.value = isNaN(Number(p.value)) ? p.value : Number(p.value);
        }
        return pred;
      });

      const challenge = await apiFetch<{ challenge: string }>('/zkp/challenge', { method: 'POST', body: '{}' });
      const request = {
        credentialId,
        proverDid,
        credentialSubject: subject,
        predicates: apiPredicates,
        challenge: challenge.challenge,
      };

      let authorization: { privateKey: string } | { walletSignature: string };
      if (walletMode && signer) {
        // The server phrases the message so it can rebuild it at verification.
        const { message } = await apiFetch<{ message: string }>('/zkp/authorization-message', {
          method: 'POST',
          body: JSON.stringify(request),
        });
        try {
          authorization = { walletSignature: await signer.signMessage(message) };
        } catch (e) {
          const msg = e instanceof Error ? e.message : String(e);
          throw new Error(
            msg.includes('user rejected') || msg.includes('ACTION_REJECTED')
              ? 'Signature rejected in MetaMask. No proof was generated.'
              : msg,
          );
        }
      } else {
        authorization = { privateKey };
      }

      const resp = await apiFetch<{ proof?: { id?: string }; id?: string } & Record<string, unknown>>('/zkp/proofs', {
        method: 'POST',
        body: JSON.stringify({ ...request, ...authorization, anchorOnChain: false }),
      });
      const proofId = (resp.proof?.id ?? resp.id) as string | undefined;
      if (proofId) setGeneratedProofId(proofId);
      setResult(JSON.stringify(resp, null, 2));
    } catch (e) {
      setError(e instanceof Error ? e.message : t('prove.errorGenerateFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleAnchor = async () => {
    if (!generatedProofId) return;
    setAnchorBusy(true);
    setAnchorError(null);
    setPendingPolls(0);
    try {
      const idemKey = crypto.randomUUID();
      const resp = await apiFetch<AnchorRecord>(`/zkp/proofs/${encodePathSegment(generatedProofId)}/anchor`, {
        method: 'POST',
        headers: { 'Idempotency-Key': idemKey },
        body: JSON.stringify({}),
      });
      setAnchor(resp);
      // Tell the page-level anchored-proofs list to refetch; it is a sibling
      // component, so it cannot observe this state directly.
      onAnchored?.();
    } catch (e) {
      setAnchorError(e instanceof Error ? e.message : t('anchor.errorSubmitFailed'));
    } finally {
      setAnchorBusy(false);
    }
  };

  const isValid = credentialId.trim() && proverDid.trim() && (walletMode ? walletMatches : privateKey.trim());
  const pollExhausted = anchor?.status === 'pending' && pendingPolls >= 12;

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <div className="space-y-4">
        <div className="rounded-xl border border-border-subtle bg-white p-6 shadow-sm">
          <h3 className="mb-4 text-sm font-bold text-text-main">{t('prove.credentialDetails')}</h3>
          <div className="space-y-3">
            <div>
              <label className="mb-1 block text-xs font-bold text-text-faint">
                {t('prove.credentialIdLabel')} <span className="text-red-400">*</span>
              </label>
              <select
                className={`w-full rounded-lg border px-3 py-2 text-sm transition-colors focus:outline-none focus:ring-1 ${
                  touched.credentialId && !credentialId.trim()
                    ? 'border-red-300 focus:border-red-400 focus:ring-red-200'
                    : 'border-border-subtle focus:border-primary focus:ring-primary'
                }`}
                value={credentialId}
                onChange={(e) => void handleCredentialSelect(e.target.value)}
                onBlur={() => markTouched('credentialId')}
                disabled={!proverDid}
              >
                <option value="">
                  {!proverDid ? 'Select a Prover DID first' : credsLoading ? 'Loading credentials…' : credentials.length === 0 ? 'No active credentials for this DID (revoked ones cannot be proven)' : 'Select a credential…'}
                </option>
                {credentials.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.schema || 'Credential'} — {c.id.slice(-12)}
                  </option>
                ))}
              </select>
              <FieldError show={!!touched.credentialId && !credentialId.trim()} message={t('prove.credentialIdRequired')} />
            </div>
            <div>
              <label className="mb-1 block text-xs font-bold text-text-faint">
                {t('prove.proverDidLabel')} <span className="text-red-400">*</span>
              </label>
              <DIDSelector
                value={proverDid}
                onChange={setProverDid}
                onBlur={() => markTouched('proverDid')}
                hasError={!!touched.proverDid && !proverDid.trim()}
                placeholder={t('prove.proverDidPlaceholder')}
              />
              <FieldError show={!!touched.proverDid && !proverDid.trim()} message={t('prove.proverDidRequired')} />
            </div>
            {walletMode ? (
              <div>
                <label className="mb-1 block text-xs font-bold text-text-faint">Authorization</label>
                {!account ? (
                  <div className="flex items-center justify-between rounded-lg border border-amber-200 bg-amber-50 p-3">
                    <span className="text-xs text-amber-800">Connect the MetaMask wallet that controls this DID to sign the proof.</span>
                    <button type="button" onClick={() => void connect()} className="ml-3 shrink-0 rounded-lg bg-amber-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-amber-700">
                      Connect
                    </button>
                  </div>
                ) : !walletMatches ? (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
                    MetaMask is on account <span className="font-mono">{account.slice(0, 6)}…{account.slice(-4)}</span>, but this DID is
                    controlled by <span className="font-mono">{proverWallet!.slice(0, 6)}…{proverWallet!.slice(-4)}</span>. Switch to that account in MetaMask.
                  </div>
                ) : (
                  <div className="flex items-start gap-2 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-800">
                    <span className="material-symbols-outlined" style={{ fontSize: 16 }}>account_balance_wallet</span>
                    <span>
                      <span className="font-bold">No private key needed.</span> MetaMask will ask you to sign a message authorizing
                      this proof. Signing is free and sends no transaction.
                    </span>
                  </div>
                )}
                <button type="button" onClick={() => setUseKeyInstead(true)} className="mt-1 text-[11px] text-text-faint hover:text-text-main hover:underline">
                  Use my Ed25519 private key instead
                </button>
              </div>
            ) : (
              <div>
                <label className="mb-1 block text-xs font-bold text-text-faint">
                  {t('prove.privateKeyLabel')} <span className="text-red-400">*</span>
                </label>
                <div className="relative">
                  <input
                    type={showKey ? 'text' : 'password'}
                    className={`w-full rounded-lg border px-3 py-2 pr-16 text-sm font-mono transition-colors focus:outline-none focus:ring-1 ${
                      touched.privateKey && !privateKey.trim()
                        ? 'border-red-300 focus:border-red-400 focus:ring-red-200'
                        : 'border-border-subtle focus:border-primary focus:ring-primary'
                    }`}
                    placeholder={t('prove.privateKeyPlaceholder')}
                    value={privateKey}
                    onChange={(e) => setPrivateKey(e.target.value)}
                    onBlur={() => markTouched('privateKey')}
                  />
                  <button
                    type="button"
                    onClick={() => setShowKey((v) => !v)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] font-bold text-text-faint hover:text-text-main"
                  >
                    {showKey ? 'Hide' : 'Show'}
                  </button>
                </div>
                <p className="mt-1 text-[10px] text-text-faint">
                  Ed25519 private key (base64url or hex). This is the key used when you created your DID — it is never stored by the platform.
                </p>
                {proverWallet && (
                  <button type="button" onClick={() => setUseKeyInstead(false)} className="mt-1 text-[11px] font-bold text-primary hover:underline">
                    Sign with MetaMask instead
                  </button>
                )}
                <FieldError show={!!touched.privateKey && !privateKey.trim()} message={t('prove.privateKeyRequired')} />
              </div>
            )}
            <div>
              <label className="mb-1 block text-xs font-bold text-text-faint">{t('prove.subjectLabel')}</label>
              <textarea
                className={`w-full rounded-lg border border-border-subtle px-3 py-2 font-mono text-sm focus:outline-none ${
                  credentialId
                    ? 'bg-slate-50 text-text-faint cursor-not-allowed'
                    : 'focus:border-primary focus:ring-1 focus:ring-primary'
                }`}
                rows={4}
                value={subjectJson}
                onChange={(e) => setSubjectJson(e.target.value)}
                readOnly={!!credentialId}
              />
              {credentialId && (
                <p className="mt-1 text-xs text-text-faint">
                  These values come from the issued credential and cannot be edited. The proof is generated against the issuer-signed claims on the server.
                </p>
              )}
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={anchorOnChain}
                onChange={(e) => setAnchorOnChain(e.target.checked)}
                className="rounded"
              />
              <span className="font-bold text-text-faint">{t('prove.anchorAfter')}</span>
            </label>
          </div>
        </div>

        <div className="rounded-xl border border-border-subtle bg-white p-6 shadow-sm">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-sm font-bold text-text-main">{t('prove.predicates')}</h3>
            <button onClick={addPredicate} className="text-xs font-bold text-primary hover:underline">
              {t('prove.addPredicate')}
            </button>
          </div>
          <div className="space-y-3">
            {predicates.map((pred, idx) => (
              <div key={pred.id} className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-xs font-bold text-text-faint">{t('prove.predicateLabel', { index: idx + 1 })}</span>
                  {predicates.length > 1 && (
                    <button onClick={() => removePredicate(idx)} className="text-xs text-red-500 hover:underline">
                      {t('prove.remove')}
                    </button>
                  )}
                </div>
                <div className="grid gap-2 sm:grid-cols-2">
                  <input
                    type="text"
                    className="rounded border border-border-subtle px-2 py-1.5 text-sm"
                    placeholder={t('prove.attributePlaceholder')}
                    value={pred.attributeName}
                    onChange={(e) => updatePredicate(idx, 'attributeName', e.target.value)}
                  />
                  <select
                    className="rounded border border-border-subtle px-2 py-1.5 text-sm"
                    value={pred.type}
                    onChange={(e) => {
                      e.preventDefault();
                      updatePredicate(idx, 'type', e.target.value);
                    }}
                  >
                    {PREDICATE_TYPES.map((pt) => (
                      <option key={pt} value={pt}>{t(`predicateTypes.${pt}`)}</option>
                    ))}
                  </select>
                  {pred.type === 'range' ? (
                    <>
                      <input
                        type="text"
                        className="rounded border border-border-subtle px-2 py-1.5 text-sm"
                        placeholder={t('prove.minPlaceholder')}
                        value={pred.rangeMin ?? ''}
                        onChange={(e) => updatePredicate(idx, 'rangeMin', e.target.value)}
                      />
                      <input
                        type="text"
                        className="rounded border border-border-subtle px-2 py-1.5 text-sm"
                        placeholder={t('prove.maxPlaceholder')}
                        value={pred.rangeMax ?? ''}
                        onChange={(e) => updatePredicate(idx, 'rangeMax', e.target.value)}
                      />
                    </>
                  ) : pred.type === 'membership' || pred.type === 'non_membership' ? (
                    <input
                      type="text"
                      className="col-span-2 rounded border border-border-subtle px-2 py-1.5 text-sm"
                      placeholder={t('prove.membershipPlaceholder')}
                      value={pred.memberSet ?? ''}
                      onChange={(e) => updatePredicate(idx, 'memberSet', e.target.value)}
                    />
                  ) : (
                    <input
                      type="text"
                      className="rounded border border-border-subtle px-2 py-1.5 text-sm"
                      placeholder={t('prove.valuePlaceholder')}
                      value={pred.value ?? ''}
                      onChange={(e) => updatePredicate(idx, 'value', e.target.value)}
                    />
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        <button
          onClick={handleGenerate}
          disabled={loading}
          className={`btn-primary w-full transition-opacity ${!isValid && !loading ? 'opacity-50 cursor-not-allowed' : ''} disabled:opacity-50`}
        >
          {loading ? (
            <span className="flex items-center justify-center gap-2">
              <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></span>
              {t('prove.generating')}
            </span>
          ) : (
            t('prove.generate')
          )}
        </button>
      </div>

      <div className="space-y-4">
        <div className="rounded-xl border border-border-subtle bg-white p-6 shadow-sm">
          <h3 className="mb-4 text-sm font-bold text-text-main">{t('prove.result')}</h3>
          {error && (
            <div className="mb-4 rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700">{error}</div>
          )}
          {result ? (
            <div className="relative">
              <CopyButton text={result} />
              <pre className="max-h-[600px] overflow-auto rounded-lg bg-slate-900 p-4 text-xs text-emerald-400 font-mono">
                {result}
              </pre>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-20 text-text-faint">
              <span className="material-symbols-outlined mb-2" style={{ fontSize: 48 }}>shield_lock</span>
              <p className="text-sm">{t('prove.emptyTitle')}</p>
              <p className="mt-2 text-xs">{t('prove.emptyHint')}</p>
            </div>
          )}
        </div>

        {generatedProofId && (
          <div className="rounded-xl border border-border-subtle bg-white p-6 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-bold text-text-main">{t('anchor.title')}</h3>
                <p className="mt-1 text-xs text-text-faint">{t('anchor.helpText')}</p>
              </div>
              {anchor && (
                <ZKAnchorBadge
                  status={anchor.status as ZKAnchorStatus}
                  blockNumber={anchor.blockNumber}
                  revokedBlock={anchor.revokedBlock}
                />
              )}
            </div>

            {!anchor && (
              <button
                onClick={handleAnchor}
                disabled={anchorBusy}
                className={`btn-primary mt-4 w-full ${anchorOnChain ? '' : 'opacity-90'} disabled:opacity-50`}
                aria-busy={anchorBusy}
              >
                {anchorBusy ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></span>
                    {t('anchor.submitting')}
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <span className="material-symbols-outlined" style={{ fontSize: 16 }}>link</span>
                    {t('anchor.button')}
                  </span>
                )}
              </button>
            )}

            {anchor && (
              <div className="mt-4 space-y-2 text-xs">
                <div className="flex items-center justify-between">
                  <span className="text-text-faint">{t('anchor.txHash')}</span>
                  {(() => {
                    const link = txUrl(anchor.txHash);
                    return link ? (
                      <a href={link} target="_blank" rel="noopener noreferrer" className="font-mono text-primary hover:underline">{shortHash(anchor.txHash)}</a>
                    ) : (
                      <span className="font-mono text-text-main">{shortHash(anchor.txHash)}</span>
                    );
                  })()}
                </div>
                {anchor.blockNumber != null && (
                  <div className="flex items-center justify-between">
                    <span className="text-text-faint">{t('anchor.block')}</span>
                    <span className="font-mono text-text-main">{anchor.blockNumber}</span>
                  </div>
                )}
                {pollExhausted && (
                  <div className="rounded-md bg-amber-50 border border-amber-200 p-2 text-amber-700">
                    {t('anchor.pollExhausted')}
                  </div>
                )}
              </div>
            )}

            {anchorError && (
              <div className="mt-3 rounded-md bg-red-50 border border-red-200 p-2 text-xs text-red-700">{anchorError}</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function VerifyTab() {
  const { t } = useTranslation('zk');
  const [proofJson, setProofJson] = useState('');
  const [proverDid, setProverDid] = useState('');
  const [result, setResult] = useState<Record<string, unknown> | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  const handleVerify = async () => {
    setTouched({ proverDid: true, proofJson: true });

    if (!proofJson.trim() || !proverDid.trim()) return;

    setLoading(true);
    setError(null);
    setResult(null);
    try {
      let parsed: Record<string, unknown>;
      try {
        parsed = JSON.parse(proofJson);
      } catch {
        throw new Error(t('verify.errorInvalidJson'));
      }
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        throw new Error(t('verify.errorMustBeObject'));
      }
      // Unwrap if user pasted the full API response envelope ({ proof: {...}, proofId: ... })
      const proof = (parsed.proof && typeof parsed.proof === 'object' && !Array.isArray(parsed.proof))
        ? parsed.proof as Record<string, unknown>
        : parsed;
      if (!proof.id || !proof.type || !proof.proverDid || !proof.proofData) {
        throw new Error(t('verify.errorMissingFields'));
      }

      const resp = await apiFetch<Record<string, unknown>>('/zkp/verify', {
        method: 'POST',
        body: JSON.stringify({ proof, proverDid }),
      });
      setResult(resp);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('verify.errorVerifyFailed'));
    } finally {
      setLoading(false);
    }
  };

  const isValid = proofJson.trim() && proverDid.trim();

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <div className="space-y-4">
        <div className="rounded-xl border border-border-subtle bg-white p-6 shadow-sm">
          <h3 className="mb-4 text-sm font-bold text-text-main">{t('verify.title')}</h3>
          <div className="space-y-3">
            <div>
              <label className="mb-1 block text-xs font-bold text-text-faint">
                {t('verify.proverDidLabel')} <span className="text-red-400">*</span>
              </label>
              <DIDSelector
                value={proverDid}
                onChange={setProverDid}
                onBlur={() => setTouched((tt) => ({ ...tt, proverDid: true }))}
                hasError={!!touched.proverDid && !proverDid.trim()}
                placeholder={t('verify.proverDidPlaceholder')}
              />
              <FieldError show={!!touched.proverDid && !proverDid.trim()} message={t('verify.proverDidRequired')} />
            </div>
            <div>
              <label className="mb-1 block text-xs font-bold text-text-faint">
                {t('verify.proofLabel')} <span className="text-red-400">*</span>
              </label>
              <textarea
                className={`w-full rounded-lg border px-3 py-2 font-mono text-sm transition-colors focus:outline-none focus:ring-1 ${
                  touched.proofJson && !proofJson.trim()
                    ? 'border-red-300 focus:border-red-400 focus:ring-red-200'
                    : 'border-border-subtle focus:border-primary focus:ring-primary'
                }`}
                rows={16}
                placeholder={t('verify.proofPlaceholder')}
                value={proofJson}
                onChange={(e) => setProofJson(e.target.value)}
                onBlur={() => setTouched((tt) => ({ ...tt, proofJson: true }))}
              />
              <FieldError show={!!touched.proofJson && !proofJson.trim()} message={t('verify.proofRequired')} />
            </div>
          </div>
        </div>

        <button
          onClick={handleVerify}
          disabled={loading}
          className={`btn-primary w-full transition-opacity ${!isValid && !loading ? 'opacity-50 cursor-not-allowed' : ''} disabled:opacity-50`}
        >
          {loading ? (
            <span className="flex items-center justify-center gap-2">
              <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></span>
              {t('verify.verifying')}
            </span>
          ) : (
            t('verify.verify')
          )}
        </button>
      </div>

      <div className="rounded-xl border border-border-subtle bg-white p-6 shadow-sm">
        <h3 className="mb-4 text-sm font-bold text-text-main">{t('verify.result')}</h3>
        {error && (
          <div className="mb-4 rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700">{error}</div>
        )}
        {result ? (
          <div className="space-y-4">
            <div
              className={`rounded-lg p-4 text-center text-sm font-bold ${
                result.valid
                  ? 'bg-emerald-50 border border-emerald-200 text-emerald-700'
                  : 'bg-red-50 border border-red-200 text-red-700'
              }`}
            >
              <span className="material-symbols-outlined mr-2 align-middle" style={{ fontSize: 20 }}>
                {result.valid ? 'check_circle' : 'cancel'}
              </span>
              {result.valid ? t('verify.valid') : t('verify.invalid')}
            </div>

            <div className="space-y-2 text-sm">
              <div className="flex justify-between border-b border-border-subtle pb-2">
                <span className="text-text-faint">{t('verify.proofId')}</span>
                <span className="font-mono text-xs text-text-main">{String(result.proofId ?? '')}</span>
              </div>
              <div className="flex justify-between border-b border-border-subtle pb-2">
                <span className="text-text-faint">{t('verify.proverDid')}</span>
                <span className="font-mono text-xs text-text-main">{String(result.proverDid ?? '')}</span>
              </div>
              <div className="flex justify-between border-b border-border-subtle pb-2">
                <span className="text-text-faint">{t('verify.credentialId')}</span>
                <span className="font-mono text-xs text-text-main">{String(result.credentialId ?? '')}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-text-faint">{t('verify.verifiedAt')}</span>
                <span className="text-xs text-text-main">{String(result.verifiedAt ?? '')}</span>
              </div>
            </div>

            {Array.isArray(result.predicateResults) && result.predicateResults.length > 0 && (
              <div>
                <h4 className="mb-2 text-xs font-bold text-text-faint uppercase tracking-wider">{t('verify.predicateResults')}</h4>
                <div className="space-y-2">
                  {(result.predicateResults as Array<Record<string, unknown>>).map((pr, i) => (
                    <div
                      key={i}
                      className={`flex items-center justify-between rounded-lg border p-3 text-sm ${
                        pr.satisfied ? 'border-emerald-200 bg-emerald-50' : 'border-red-200 bg-red-50'
                      }`}
                    >
                      <div>
                        <span className="font-bold text-text-main">{String(pr.attributeName)}</span>
                        <span className="ml-2 text-xs text-text-faint">({String(pr.predicateType)})</span>
                      </div>
                      <span className={`text-xs font-bold ${pr.satisfied ? 'text-emerald-700' : 'text-red-700'}`}>
                        {pr.satisfied ? t('verify.satisfied') : String(pr.error ?? t('verify.failed'))}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {Array.isArray(result.errors) && result.errors.length > 0 && (
              <div className="rounded-lg bg-red-50 border border-red-200 p-3">
                <h4 className="mb-1 text-xs font-bold text-red-700 uppercase">{t('verify.errors')}</h4>
                {(result.errors as string[]).map((e, i) => (
                  <p key={i} className="text-xs text-red-600">{e}</p>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-20 text-text-faint">
            <span className="material-symbols-outlined mb-2" style={{ fontSize: 48 }}>verified</span>
            <p className="text-sm">{t('verify.emptyTitle')}</p>
            <p className="mt-2 text-xs">{t('verify.emptyHint')}</p>
          </div>
        )}
      </div>
    </div>
  );
}

interface DelegatedProofResponse {
  proofId: string;
  delegationId?: string;
  proof?: Record<string, unknown>;
  anchor?: AnchorRecord | null;
}

function DelegateTab() {
  const { t } = useTranslation('zk');
  const [holderDid, setHolderDid] = useState('');
  const [agentDid, setAgentDid] = useState('');
  const [credentialId, setCredentialId] = useState('');
  const [ibctText, setIbctText] = useState('');
  const [predicate, setPredicate] = useState<Predicate>({
    id: 'delegate-predicate',
    attributeName: 'age',
    type: 'gte',
    value: '18',
  });
  const [anchorAfter, setAnchorAfter] = useState(true);
  const [resp, setResp] = useState<DelegatedProofResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    setError(null);
    setResp(null);
    if (!holderDid.trim() || !agentDid.trim() || !ibctText.trim() || !predicate.attributeName) {
      setError(t('delegate.errorMissingField'));
      return;
    }

    let ibct: unknown;
    const trimmed = ibctText.trim();
    if (trimmed.startsWith('{')) {
      try {
        ibct = JSON.parse(trimmed);
      } catch {
        setError(t('delegate.errorIbctJson'));
        return;
      }
    } else {
      ibct = trimmed; // accept raw JWT string
    }

    const apiPredicate: Record<string, unknown> = {
      attributeName: predicate.attributeName,
      type: predicate.type,
    };
    if (predicate.type === 'range') {
      apiPredicate.rangeMin = Number(predicate.rangeMin);
      apiPredicate.rangeMax = Number(predicate.rangeMax);
    } else if (predicate.type === 'membership' || predicate.type === 'non_membership') {
      apiPredicate.memberSet = predicate.memberSet?.split(',').map((s) => s.trim()) ?? [];
    } else {
      apiPredicate.value = isNaN(Number(predicate.value)) ? predicate.value : Number(predicate.value);
    }

    setLoading(true);
    try {
      const result = await apiFetch<DelegatedProofResponse>('/zkp/delegated-proofs', {
        method: 'POST',
        body: JSON.stringify({
          holderDid,
          agentDid,
          credentialId: credentialId || undefined,
          predicate: apiPredicate,
          ibct,
          anchor: anchorAfter,
        }),
      });
      setResp(result);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('delegate.errorSubmit'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <div className="space-y-4">
        <div className="rounded-xl border border-border-subtle bg-white p-6 shadow-sm">
          <h3 className="mb-1 text-sm font-bold text-text-main">{t('delegate.formTitle')}</h3>
          <p className="mb-4 text-xs text-text-faint">{t('delegate.formHelp')}</p>

          <div className="space-y-3">
            <div>
              <label className="mb-1 block text-xs font-bold text-text-faint">
                {t('delegate.holderDidLabel')} <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                className="w-full rounded-lg border border-border-subtle px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                placeholder="did:adi:holder-..."
                value={holderDid}
                onChange={(e) => setHolderDid(e.target.value)}
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-bold text-text-faint">
                {t('delegate.agentDidLabel')} <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                className="w-full rounded-lg border border-border-subtle px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                placeholder="did:adi:agent-..."
                value={agentDid}
                onChange={(e) => setAgentDid(e.target.value)}
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-bold text-text-faint">
                {t('delegate.credentialIdLabel')}
              </label>
              <input
                type="text"
                className="w-full rounded-lg border border-border-subtle px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                placeholder="urn:uuid:..."
                value={credentialId}
                onChange={(e) => setCredentialId(e.target.value)}
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-bold text-text-faint">
                {t('delegate.ibctLabel')} <span className="text-red-400">*</span>
              </label>
              <textarea
                rows={6}
                className="w-full rounded-lg border border-border-subtle px-3 py-2 font-mono text-xs focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                placeholder={t('delegate.ibctPlaceholder')}
                value={ibctText}
                onChange={(e) => setIbctText(e.target.value)}
              />
              <p className="mt-1 text-[11px] text-text-faint">{t('delegate.ibctHint')}</p>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={anchorAfter}
                onChange={(e) => setAnchorAfter(e.target.checked)}
                className="rounded"
              />
              <span className="font-bold text-text-faint">{t('delegate.anchorAfter')}</span>
            </label>
          </div>
        </div>

        <div className="rounded-xl border border-border-subtle bg-white p-6 shadow-sm">
          <h3 className="mb-3 text-sm font-bold text-text-main">{t('delegate.predicateTitle')}</h3>
          <div className="grid gap-2 sm:grid-cols-2">
            <input
              type="text"
              className="rounded border border-border-subtle px-2 py-1.5 text-sm"
              placeholder={t('prove.attributePlaceholder')}
              value={predicate.attributeName}
              onChange={(e) => setPredicate({ ...predicate, attributeName: e.target.value })}
            />
            <select
              className="rounded border border-border-subtle px-2 py-1.5 text-sm"
              value={predicate.type}
              onChange={(e) => setPredicate({ ...predicate, type: e.target.value })}
            >
              {PREDICATE_TYPES.map((pt) => (
                <option key={pt} value={pt}>{t(`predicateTypes.${pt}`)}</option>
              ))}
            </select>
            {predicate.type === 'range' ? (
              <>
                <input
                  type="text"
                  className="rounded border border-border-subtle px-2 py-1.5 text-sm"
                  placeholder={t('prove.minPlaceholder')}
                  value={predicate.rangeMin ?? ''}
                  onChange={(e) => setPredicate({ ...predicate, rangeMin: e.target.value })}
                />
                <input
                  type="text"
                  className="rounded border border-border-subtle px-2 py-1.5 text-sm"
                  placeholder={t('prove.maxPlaceholder')}
                  value={predicate.rangeMax ?? ''}
                  onChange={(e) => setPredicate({ ...predicate, rangeMax: e.target.value })}
                />
              </>
            ) : predicate.type === 'membership' || predicate.type === 'non_membership' ? (
              <input
                type="text"
                className="col-span-2 rounded border border-border-subtle px-2 py-1.5 text-sm"
                placeholder={t('prove.membershipPlaceholder')}
                value={predicate.memberSet ?? ''}
                onChange={(e) => setPredicate({ ...predicate, memberSet: e.target.value })}
              />
            ) : (
              <input
                type="text"
                className="rounded border border-border-subtle px-2 py-1.5 text-sm"
                placeholder={t('prove.valuePlaceholder')}
                value={predicate.value ?? ''}
                onChange={(e) => setPredicate({ ...predicate, value: e.target.value })}
              />
            )}
          </div>
        </div>

        <button
          onClick={handleSubmit}
          disabled={loading}
          className="btn-primary w-full disabled:opacity-50"
        >
          {loading ? (
            <span className="flex items-center justify-center gap-2">
              <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
              {t('delegate.submitting')}
            </span>
          ) : (
            t('delegate.submit')
          )}
        </button>
      </div>

      <div className="rounded-xl border border-border-subtle bg-white p-6 shadow-sm">
        <h3 className="mb-4 text-sm font-bold text-text-main">{t('delegate.resultTitle')}</h3>
        {error && (
          <div className="mb-4 rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700">{error}</div>
        )}
        {!resp && !error && (
          <div className="flex flex-col items-center justify-center py-20 text-text-faint">
            <span className="material-symbols-outlined mb-2" style={{ fontSize: 48 }}>handshake</span>
            <p className="text-sm">{t('delegate.emptyTitle')}</p>
            <p className="mt-2 text-xs">{t('delegate.emptyHint')}</p>
          </div>
        )}
        {resp && (
          <div className="space-y-4">
            <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3">
              <div className="flex items-center gap-2 text-sm font-bold text-emerald-700">
                <span className="material-symbols-outlined" style={{ fontSize: 16 }}>check_circle</span>
                {t('delegate.success')}
              </div>
              <div className="mt-2 text-xs text-text-main">
                <div className="flex justify-between"><span className="text-text-faint">{t('delegate.proofId')}</span><span className="font-mono">{shortHash(resp.proofId, 10, 6)}</span></div>
                {resp.delegationId && (
                  <div className="flex justify-between"><span className="text-text-faint">{t('delegate.delegationId')}</span><span className="font-mono">{resp.delegationId}</span></div>
                )}
              </div>
            </div>

            {resp.anchor ? (
              <div className="rounded-md border border-border-subtle bg-slate-50 p-3 text-xs">
                <div className="mb-2 flex items-center justify-between">
                  <span className="font-bold text-text-main">{t('anchor.title')}</span>
                  <ZKAnchorBadge
                    status={resp.anchor.status as ZKAnchorStatus}
                    blockNumber={resp.anchor.blockNumber}
                    revokedBlock={resp.anchor.revokedBlock}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-text-faint">{t('anchor.txHash')}</span>
                  {(() => {
                    const link = txUrl(resp.anchor.txHash);
                    return link ? (
                      <a href={link} target="_blank" rel="noopener noreferrer" className="font-mono text-primary hover:underline">{shortHash(resp.anchor.txHash)}</a>
                    ) : (
                      <span className="font-mono text-text-main">{shortHash(resp.anchor.txHash)}</span>
                    );
                  })()}
                </div>
              </div>
            ) : (
              <p className="text-xs text-text-faint">{t('delegate.noAnchor')}</p>
            )}

            <details>
              <summary className="cursor-pointer text-xs font-bold text-primary">{t('delegate.viewProof')}</summary>
              <pre className="mt-2 max-h-[300px] overflow-auto rounded-lg bg-slate-900 p-3 text-[11px] text-emerald-400 font-mono">
                {JSON.stringify(resp.proof ?? {}, null, 2)}
              </pre>
            </details>
          </div>
        )}
      </div>
    </div>
  );
}

interface PaginatedAnchorList {
  items?: AnchorRecord[];
  data?: AnchorRecord[];
}

function AnchoredProofsList({ did, refreshKey = 0 }: { did: string | null; refreshKey?: number }) {
  const { t } = useTranslation('zk');
  const [items, setItems] = useState<AnchorRecord[] | null>(null);
  const [loading, setLoading] = useState(false);
  const lastFetch = useRef<string | null>(null);
  const [revoking, setRevoking] = useState<string | null>(null);
  const [revokeError, setRevokeError] = useState<string | null>(null);

  useEffect(() => {
    // Refetch when the DID changes (or first render) AND whenever refreshKey is
    // bumped — a newly anchored proof does not change the DID, so keying on the
    // DID alone would leave the list showing "No anchors yet".
    const fetchKey = `${did ?? ''}#${refreshKey}`;
    if (lastFetch.current === fetchKey) return;
    lastFetch.current = fetchKey;
    let cancelled = false;
    setLoading(true);
    const params = new URLSearchParams();
    if (did) params.set('did', did);
    params.set('limit', '50');
    apiFetch<PaginatedAnchorList | AnchorRecord[]>(`/zkp/anchors?${params.toString()}`)
      .then((res) => {
        if (cancelled) return;
        const list: AnchorRecord[] = Array.isArray(res)
          ? res
          : Array.isArray(res?.items)
            ? res.items
            : Array.isArray(res?.data)
              ? res.data
              : [];
        setItems(list);
      })
      .catch(() => {
        if (cancelled) return;
        setItems([]);
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [did]);

  const handleRevoke = async (proofId: string) => {
    if (!window.confirm('Revoke this anchored proof? This cannot be undone.')) return;
    setRevoking(proofId);
    setRevokeError(null);
    try {
      await apiFetch(`/zkp/proofs/${encodePathSegment(proofId)}/revoke`, {
        method: 'POST',
        body: JSON.stringify({ reason: 'user_revoked' }),
      });
      // Refresh list after revoke
      lastFetch.current = null;
      setItems(null);
    } catch (e) {
      setRevokeError(e instanceof Error ? e.message : 'Revoke failed');
    } finally {
      setRevoking(null);
    }
  };

  return (
    <div className="rounded-xl border border-border-subtle bg-white p-6 shadow-sm">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="text-sm font-bold text-text-main">{t('anchor.listTitle')}</h3>
        {!loading && items && (
          <span className="text-[11px] text-text-faint">{t('anchor.listCount', { count: items.length })}</span>
        )}
      </div>
      {loading ? (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
        </div>
      ) : !items || items.length === 0 ? (
        <p className="py-6 text-center text-xs text-text-faint">{t('anchor.listEmpty')}</p>
      ) : (
        <div className="overflow-x-auto">
          {revokeError && (
            <p className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-xs font-semibold text-red-600">{revokeError}</p>
          )}
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-border-subtle text-text-faint">
                <th className="px-3 py-2 text-left font-bold uppercase tracking-wider">{t('anchor.col.proofId')}</th>
                <th className="px-3 py-2 text-left font-bold uppercase tracking-wider">{t('anchor.col.tx')}</th>
                <th className="px-3 py-2 text-left font-bold uppercase tracking-wider">{t('anchor.col.block')}</th>
                <th className="px-3 py-2 text-left font-bold uppercase tracking-wider">{t('anchor.col.status')}</th>
                <th className="px-3 py-2 text-left font-bold uppercase tracking-wider">{t('anchor.col.time')}</th>
                <th className="px-3 py-2 w-20"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((row) => {
                const link = txUrl(row.txHash);
                const isConfirmed = row.status === 'confirmed';
                const isRevoking = revoking === row.proofId;
                return (
                  <tr key={`${row.proofId}-${row.txHash}`} className="border-b border-border-subtle/60 hover:bg-slate-50/60">
                    <td className="px-3 py-2 font-mono text-text-main">{shortHash(row.proofId, 12, 6)}</td>
                    <td className="px-3 py-2 font-mono">
                      {link ? (
                        <a href={link} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">{shortHash(row.txHash)}</a>
                      ) : (
                        <span className="text-text-main">{shortHash(row.txHash)}</span>
                      )}
                    </td>
                    <td className="px-3 py-2 font-mono text-text-main">{row.blockNumber ?? '—'}</td>
                    <td className="px-3 py-2">
                      <ZKAnchorBadge
                        status={row.status as ZKAnchorStatus}
                        blockNumber={row.blockNumber}
                        revokedBlock={row.revokedBlock}
                        compact
                      />
                    </td>
                    <td className="px-3 py-2 text-text-faint">{row.submittedAt ?? ''}</td>
                    <td className="px-3 py-2">
                      {isConfirmed && (
                        <button
                          onClick={() => void handleRevoke(row.proofId)}
                          disabled={isRevoking}
                          title="Revoke proof on-chain"
                          className="rounded px-2 py-1 text-[10px] font-bold text-red-600 border border-red-200 hover:bg-red-50 disabled:opacity-40"
                        >
                          {isRevoking ? '…' : 'Revoke'}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
